import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Neutral } from '@sorare/core/src/atoms/icons/Neutral';
import { Promotion } from '@sorare/core/src/atoms/icons/Promotion';
import { Relegation } from '@sorare/core/src/atoms/icons/Relegation';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { DateRange } from '@sorare/core/src/components/lobby/DateRange';
import { rivals } from '@sorare/core/src/lib/glossary';

import { UserSeasonEntry_footballRivalsSeason } from './__generated__/index.graphql';

const Root = styled.button`
  width: 100%;
  display: flex;
  gap: var(--double-unit);
  align-items: center;
  padding: var(--double-unit);
  text-align: left;

  &:not(.disabled):hover {
    background: rgba(var(--c-rgb-white), 0.07);
  }
  &.selected {
    background: rgba(var(--c-rgb-white), 0.2);
  }
`;
const Wrapper = styled.div`
  width: var(--triple-unit);
`;
const Badge = styled.img`
  width: var(--triple-unit);
`;
const Info = styled(Vertical).attrs({ gap: 0 })`
  flex: 1;
`;

type Props = {
  season: UserSeasonEntry_footballRivalsSeason;
  onClick: () => void;
  i: number;
  selected?: boolean;
};
export const UserSeasonEntry = ({ season, onClick, i, selected }: Props) => {
  const isLive = i === 0;
  const isPast = i === 1;
  const { ranking } = season || {};

  const seasonInterval =
    season.startDate && season.cutOffDate ? (
      <DateRange startDate={season.startDate} endDate={season.cutOffDate} />
    ) : undefined;

  return (
    <Root onClick={onClick} className={classNames({ selected })} type="button">
      <Wrapper>
        {ranking?.divisionChange === DivisionChange.PROMOTED && (
          <Promotion fill="var(--c-rivals-friendlies)" />
        )}
        {ranking?.divisionChange === DivisionChange.RELEGATED && <Relegation />}
        {ranking?.divisionChange === DivisionChange.STABLE && <Neutral />}
      </Wrapper>
      <Info>
        {isLive && (
          <LabelM>
            <FormattedMessage {...rivals.currentSeason} />
          </LabelM>
        )}
        {isPast && (
          <LabelM>
            <FormattedMessage {...rivals.pastSeason} />
          </LabelM>
        )}
        {!(isLive || isPast) ? (
          <LabelM>{seasonInterval}</LabelM>
        ) : (
          <LabelS color="var(--c-nd-600)">{seasonInterval}</LabelS>
        )}
      </Info>
      <Wrapper>
        {ranking?.divisionConfig.badgeUrl && (
          <Badge src={ranking.divisionConfig.badgeUrl} alt="" />
        )}
      </Wrapper>
    </Root>
  );
};

UserSeasonEntry.fragments = {
  footballRivalsSeason: gql`
    fragment UserSeasonEntry_footballRivalsSeason on FootballRivalsSeason {
      id
      slug
      startDate
      cutOffDate
      ranking(userSlug: $userSlug) @include(if: $isUserQuery) {
        id
        divisionChange
        divisionConfig {
          id
          badgeUrl
        }
      }
    }
  ` as TypedDocumentNode<UserSeasonEntry_footballRivalsSeason>,
};
